import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { Meta } from '@/components/seo/Meta';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { FadeIn, Stagger, StaggerItem } from '@/components/motion/FadeIn';
import { TextSplit, MaskReveal } from '@/components/motion/Reveal';
import { Skeleton } from '@/components/ui/Skeleton';
import { usePublishedContents } from '@/lib/cms';
import type { Content, ContentCategory } from '@/lib/cms-types';

const CATEGORIES: Array<{ key: ContentCategory | 'all'; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'short-drama', label: 'Short dramas' },
  { key: 'micro-series', label: 'Micro-series' },
  { key: 'promo', label: 'Promos' },
  { key: 'behind-the-scenes', label: 'Behind the scenes' },
];

const CATEGORY_LABEL: Record<ContentCategory, string> = {
  'short-drama': 'Short drama',
  'micro-series': 'Micro-series',
  promo: 'Promo',
  'behind-the-scenes': 'Behind the scenes',
};

export function ContentsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = (searchParams.get('category') ?? 'all') as ContentCategory | 'all';
  const { data: items = [], isLoading } = usePublishedContents(category);

  const featured = useMemo(() => items.find((c) => c.is_featured) ?? null, [items]);
  const rest = useMemo(
    () => (featured ? items.filter((c) => c.id !== featured.id) : items),
    [items, featured],
  );

  function setCategory(c: ContentCategory | 'all') {
    if (c === 'all') searchParams.delete('category');
    else searchParams.set('category', c);
    setSearchParams(searchParams, { replace: true });
  }

  return (
    <>
      <Meta
        title="Contents"
        description="Short dramas and micro-series produced by Vonlof's Content Creation department — with the talent behind them."
        path="/contents"
      />
      <main className="pt-24 pb-12 sm:pt-28 md:pt-32 md:pb-20">
        <MaskReveal className="container-wide" duration={1}>
          <FadeIn>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Content Creation</p>
          </FadeIn>
          <TextSplit
            text="Drama, with the faces behind it."
            className="mt-3 display-serif text-display-l text-fg"
            delay={0.1}
            stagger={0.06}
          />
          <FadeIn delay={0.3}>
            <p className="mt-6 max-w-2xl text-lg text-fg-muted">
              Short dramas and micro-series produced by our Content Creation department — and the influencers we work with to bring them to life.
            </p>
          </FadeIn>

          {/* CATEGORIES */}
          <FadeIn delay={0.2}>
            <div
              className="-mx-4 mt-10 flex gap-2 overflow-x-auto px-4 pb-2 scrollbar-hide scroll-x-mask sm:mx-0 sm:mt-12 sm:flex-wrap sm:overflow-visible sm:px-0 sm:[mask-image:none]"
              role="tablist"
              aria-label="Filter by category"
            >
              {CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  role="tab"
                  aria-selected={category === c.key}
                  onClick={() => setCategory(c.key)}
                  className={
                    category === c.key
                      ? 'rounded-full border border-gold-500/40 bg-gold-500/10 px-3 py-1.5 text-xs font-medium text-gold-500'
                      : 'rounded-full border border-border bg-bg-elevated px-3 py-1.5 text-xs font-medium text-fg-muted transition-colors hover:border-gold-500/40 hover:text-fg'
                  }
                >
                  {c.label}
                </button>
              ))}
            </div>
          </FadeIn>

          {/* FEATURED */}
          {!isLoading && featured && (
            <FeaturedContent content={featured} />
          )}

          {/* GRID */}
          {isLoading ? (
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Card key={i} className="h-full overflow-hidden">
                  <Skeleton className="aspect-video w-full" />
                  <CardHeader>
                    <Skeleton className="h-4 w-1/3" />
                    <Skeleton className="mt-2 h-5 w-3/4" />
                    <Skeleton className="mt-2 h-4 w-full" />
                  </CardHeader>
                </Card>
              ))}
            </div>
          ) : items.length === 0 ? (
            <Card className="mt-12 p-12 text-center text-fg-subtle">
              No content in this category yet.
            </Card>
          ) : (
            <Stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {rest.map((c) => (
                <StaggerItem key={c.id}>
                  <ContentCard content={c} />
                </StaggerItem>
              ))}
            </Stagger>
          )}
        </MaskReveal>
      </main>
    </>
  );
}

function ContentCard({ content }: { content: Content }) {
  const inf = content.primary_influencer;
  return (
    <Link to={`/contents/${content.slug}`} className="group block h-full">
      <Card className="h-full overflow-hidden transition-all hover:-translate-y-1 hover:border-gold-500/40 hover:shadow-gold">
        <div className="relative aspect-video w-full overflow-hidden bg-gradient-to-br from-gold-500/20 to-bg-elevated">
          {content.cover_url ? (
            <img
              src={content.cover_url}
              alt={`${content.title} cover`}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover transition-transform duration-slow group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-xs uppercase tracking-widest text-fg-subtle">
              {CATEGORY_LABEL[content.category]}
            </div>
          )}
        </div>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="gold">{CATEGORY_LABEL[content.category]}</Badge>
          </div>
          <CardTitle className="mt-3 line-clamp-2">{content.title}</CardTitle>
          <CardDescription className="line-clamp-2">{content.summary}</CardDescription>
          {inf && (
            <div className="mt-4 flex items-center gap-2">
              <Avatar className="h-7 w-7">
                {inf.portrait_url && (
                  <AvatarImage src={inf.portrait_url} alt={inf.name} />
                )}
                <AvatarFallback>{initials(inf.name)}</AvatarFallback>
              </Avatar>
              <div className="text-xs">
                <p className="text-fg">{inf.name}</p>
                {inf.headline && <p className="text-fg-subtle">{inf.headline}</p>}
              </div>
            </div>
          )}
          <span className="mt-3 inline-flex items-center gap-1 text-sm text-gold-500">
            Watch now
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </span>
        </CardHeader>
      </Card>
    </Link>
  );
}

function FeaturedContent({ content }: { content: Content }) {
  const inf = content.primary_influencer;
  return (
    <FadeIn delay={0.25}>
      <Link to={`/contents/${content.slug}`} className="group mt-12 block">
        <div className="grid gap-8 rounded-2xl border border-border bg-bg-elevated/40 p-6 transition-colors hover:border-gold-500/40 md:grid-cols-2 md:p-8">
          <div className="relative aspect-video overflow-hidden rounded-lg bg-gradient-to-br from-gold-500/20 to-bg-elevated md:aspect-auto md:h-full">
            {content.cover_url ? (
              <img
                src={content.cover_url}
                alt={`${content.title} cover`}
                loading="eager"
                decoding="async"
                className="h-full w-full object-cover transition-transform duration-slow group-hover:scale-105"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-xs uppercase tracking-widest text-fg-subtle">
                {CATEGORY_LABEL[content.category]}
              </div>
            )}
          </div>
          <div className="flex flex-col justify-center">
            <div className="flex items-center gap-2">
              <Badge variant="gold">Featured</Badge>
              <Badge variant="outline">{CATEGORY_LABEL[content.category]}</Badge>
            </div>
            <h2 className="display-serif mt-3 text-3xl text-fg md:text-4xl">{content.title}</h2>
            <p className="mt-4 text-fg-muted">{content.summary}</p>
            {inf && (
              <div className="mt-6 flex items-center gap-3">
                <Avatar className="h-10 w-10">
                  {inf.portrait_url && (
                    <AvatarImage src={inf.portrait_url} alt={inf.name} />
                  )}
                  <AvatarFallback>{initials(inf.name)}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm font-medium text-fg">{inf.name}</p>
                  {inf.headline && <p className="text-xs text-fg-subtle">{inf.headline}</p>}
                </div>
              </div>
            )}
            <span className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-gold-500">
              Watch now →
            </span>
          </div>
        </div>
      </Link>
    </FadeIn>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}